import * as React from 'react';
import {useEffect, useState} from "react";
import {useNavigate} from 'react-router-dom';
import {
    CalendarIcon,
    UserPlusIcon,
    UsersIcon,
    ChartBarIcon,
    ClockIcon,
    ExclamationTriangleIcon,
    CheckCircleIcon,
    XCircleIcon
} from '@heroicons/react/24/outline';
import dayjs from 'dayjs';
import Helper from "../../helpers";
import LayoutWrapper from "../../components/LayoutWrapper";
import UserAdminTable from "../../components/UserAdminTable";
import AddSession from "../../components/AddSession";
import {
    getUserVenueTokens,
    getUserUpcomingSessionCount,
    getVenueUserProfile,
    fetchVenueUsers
} from "../../Services/UserService.ts";
import InviteLink from "../../components/InviteLink";
import TrainingPlanWidget from "../../components/TrainingPlanWidget";
import UpcomingPaymentsCard from "./UpcomingPaymentsCard";
import WelcomeMessage from "../../components/WelcomeMessage";
import config from "../../config";

const baseUrl = config.apiBaseUrl;

export default function DashboardHome({ venueFeatures = null }) {
    const [isAdmin, setIsAdmin] = useState(false);
    const [venueOptions, setVenueOptions] = useState([]);
    const [currentVenueKey, setCurrentVenueKey] = useState("");
    const [showAddSessionModal, setShowAddSessionModal] = useState(false);
    const [userProfile, setUserProfile] = useState({});
    const [usersVenueTokens, setUsersVenueTokens] = useState(0);
    const [bookedSessions, setBookedSessions] = useState(0);
    const [totalMembers, setTotalMembers] = useState(0);
    const [atRiskUsersCount, setAtRiskUsersCount] = useState(0);
    const [gcConnected, setGcConnected] = useState(false);
    const [venueHasTerms, setVenueHasTerms] = useState(false);
    const [venueReadinessChecked, setVenueReadinessChecked] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const checkAdmin = async () => {
            const admin = await Helper.userIsAdmin();
            await Helper.getAdminVenues(setCurrentVenueKey, setVenueOptions)
            setIsAdmin(admin);
        };

        const getVenueTokens = async () => {
            const venue = await Helper.getUserVenueId();
            const userId = await Helper.getUserId();
            if (userId) {
                const tokens = await getUserVenueTokens(venue);
                setUsersVenueTokens(tokens.tokens);
            }
        }

        const getVenueUserProfileForPage = async () => {
            const venue = await Helper.getUserVenueId();
            var user = await getVenueUserProfile(venue);
            setUserProfile(user);
        }

        const getBookedSessions = async () => {
            const userId = await Helper.getUserId();
            if (userId) {
                const sessions = await getUserUpcomingSessionCount(userId);
                setBookedSessions(sessions.count);
            }
        }

        const getTotalMembers = async () => {
            const venue = await Helper.getUserVenueId();
            const admin = await Helper.userIsAdmin();
            if (admin && venue) {
                try {
                    const users = await fetchVenueUsers(venue);
                    setTotalMembers(users?.length || 0);
                } catch (error) {
                    console.error('Error fetching total members:', error);
                }
            }
        }

        const getAtRiskUsers = async () => {
            const venue = await Helper.getUserVenueId();
            const admin = await Helper.userIsAdmin();
            if (admin && venue) {
                try {
                    const token = await Helper.getCookie('wh-jwt');
                    const response = await fetch(
                        `${baseUrl}/api/venues/${venue}/reports/users-at-risk?daysThreshold=30`,
                        {
                            method: 'GET',
                            headers: {
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'application/json'
                            }
                        }
                    );
                    if (response.ok) {
                        const data = await response.json();
                        // Count users with High or Medium risk
                        const atRiskCount = data.atRiskUsers?.filter(
                            user => user.riskLevel === 'High' || user.riskLevel === 'Medium'
                        ).length || 0;
                        setAtRiskUsersCount(atRiskCount);
                    }
                } catch (error) {
                    console.error('Error fetching at-risk users:', error);
                }
            }
        }

        const checkVenueReadiness = async () => {
            const admin = await Helper.userIsAdmin();
            if (!admin) return;
            const venue = await Helper.getUserVenueId();
            if (!venue) return;

            try {
                const token = await Helper.getCookie('wh-jwt');

                // Check GoCardless connection
                const gcRes = await fetch(`${baseUrl}/api/paymentadmin/admin/${venue}`, {
                    headers: { 'Authorization': `Bearer ${token}` },
                    credentials: 'include'
                });
                if (gcRes.ok) {
                    const gcData = await gcRes.json();
                    setGcConnected(gcData.some(p => p.name === 'GoCardless' && p.enabled));
                }

                // Check venue terms
                const termsRes = await fetch(`${baseUrl}/api/venue/public/${venue}/terms`);
                if (termsRes.ok) {
                    const termsData = await termsRes.json();
                    setVenueHasTerms(!!(termsData.hasTerms && termsData.termsContent));
                }
            } catch (e) {
                console.warn('Could not check venue readiness:', e);
            } finally {
                setVenueReadinessChecked(true);
            }
        };

        getVenueTokens();
        getBookedSessions();
        checkAdmin();
        getVenueUserProfileForPage();
        getTotalMembers();
        getAtRiskUsers();
        checkVenueReadiness();
    }, []);
    const hasFeature = (f) => venueFeatures === null || venueFeatures.includes(f);

    // Setup checklist shown to admins when invite link prerequisites are missing
    const renderInviteLinkOrChecklist = () => {
        if (!venueReadinessChecked) return null;

        const isReady = gcConnected && venueHasTerms;

        if (isReady) {
            return <InviteLink venueShortCode={currentVenueKey} />;
        }

        return (
            <div className="backdrop-blur-sm bg-gray-900/30 border border-amber-700/40 rounded-xl p-4">
                <div className="flex items-start gap-3 mb-3">
                    <ExclamationTriangleIcon className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
                    <div>
                        <p className="text-sm font-semibold text-amber-300">Invite link unavailable</p>
                        <p className="text-xs text-gray-400 mt-0.5">
                            Complete the setup steps below before sharing your signup link with new members.
                        </p>
                    </div>
                </div>
                <ul className="space-y-2">
                    <li className="flex items-center gap-2 text-sm">
                        {gcConnected
                            ? <CheckCircleIcon className="h-4 w-4 text-emerald-400 flex-shrink-0" />
                            : <XCircleIcon className="h-4 w-4 text-red-400 flex-shrink-0" />}
                        <span className={gcConnected ? 'text-emerald-300' : 'text-gray-300'}>
                            GoCardless payment provider connected
                        </span>
                        {!gcConnected && (
                            <a href="/dashboard/paymentproviderlinks"
                                className="ml-auto text-xs text-emerald-400 hover:underline whitespace-nowrap">
                                Connect →
                            </a>
                        )}
                    </li>
                    <li className="flex items-center gap-2 text-sm">
                        {venueHasTerms
                            ? <CheckCircleIcon className="h-4 w-4 text-emerald-400 flex-shrink-0" />
                            : <XCircleIcon className="h-4 w-4 text-red-400 flex-shrink-0" />}
                        <span className={venueHasTerms ? 'text-emerald-300' : 'text-gray-300'}>
                            Venue terms &amp; conditions set
                        </span>
                        {!venueHasTerms && (
                            <a href="/dashboard/adminTermsAndConditions"
                                className="ml-auto text-xs text-emerald-400 hover:underline whitespace-nowrap">
                                Set up →
                            </a>
                        )}
                    </li>
                </ul>
            </div>
        );
    };

    return (
        <LayoutWrapper
            title={`Welcome ${isAdmin ? "Admin" : "Member"}`}
            subtitle="Dashboard Overview"
        >
            {/* Premium Time-Aware Welcome Message */}
            <WelcomeMessage userName={userProfile?.name || (isAdmin ? "Admin" : "Member")} />
            
            {venueOptions.length > 0 && (
                <></>
            )}
            {/* Stats Cards */}

            {/* Quick Actions & Next Session */}
            {hasFeature("sessions") && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
                <div className="card p-4 sm:p-6">
                    <h3 className="text-base sm:text-lg font-semibold text-white mb-3 sm:mb-4">
                        <ClockIcon className="h-4 w-4 sm:h-5 sm:w-5 inline mr-2"/>
                        Next Session
                    </h3>
                    <div className="bg-gray-800 rounded-lg p-3 mb-4">
                        {userProfile && Object.keys(userProfile).length > 0 && userProfile.nextSession ? (
                            <div>
                                <a
                                    href="/dashboard/sessions?selected=mysessions">
                                    <h4 className="font-medium text-white text-sm sm:text-base">{userProfile.nextSessionName}</h4>
                                </a>
                                <p className="text-xs sm:text-sm text-gray-300 mt-1">
                                    {dayjs(userProfile.nextSession).format('dddd, MMMM D, YYYY')} at {dayjs(userProfile.nextSession).format('h:mm A')}
                                </p>
                            </div>
                        ) : (
                            <div className="text-gray-400 text-sm">No upcoming sessions</div>
                        )}
                    </div>
                    {userProfile && userProfile.nextSession && (
                        <button
                            onClick={() => navigate('/dashboard/sessions?selected=mysessions')}
                            className="btn-primary text-center py-2 text-sm sm:text-base w-full mb-3"
                        >
                            View Next Session
                        </button>
                    )}
                    {isAdmin && (
                        <>
                            {renderInviteLinkOrChecklist()}
                        </>
                    )}
                </div>

                <div className="card p-4 sm:p-6">
                    <h3 className="text-base sm:text-lg font-semibold text-white mb-3 sm:mb-4">
                        Quick Actions
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <button
                            onClick={() => navigate('/dashboard/sessions')}
                            className="btn-primary text-center py-3 text-sm sm:text-base w-full"
                        >
                            Book Session
                        </button>
                        <button
                            onClick={() => navigate('/dashboard/classes')}
                            className="btn-outline text-center py-3 text-sm sm:text-base w-full"
                        >
                            View Classes
                        </button>
                        {isAdmin && (
                            <>
                                <button
                                    onClick={() => setShowAddSessionModal(true)}
                                    className="btn-outline text-center py-3 text-sm sm:text-base w-full"
                                >
                                    Add Session
                                </button>
                                <button
                                    onClick={() => navigate('/dashboard/bulk-session')}
                                    className="bg-gray-700 hover:bg-gray-600 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 text-center text-sm sm:text-base w-full"
                                >
                                    Bulk Add Sessions
                                </button>

                                <button
                                    onClick={() => navigate('/dashboard/addmanyclasses')}
                                    className="bg-gray-700 hover:bg-gray-600 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 text-center text-sm sm:text-base w-full"
                                >
                                    Bulk Add Classes
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div> )}
            {hasFeature("sessions") && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
                <div className="card-luxury p-4 sm:p-6 hover-lift">
                    <div className="flex items-center">
                        <a
                            href="/dashboard/sessions?selected=mysessions"
                            className="flex items-center cursor-pointer w-full">
                            <div className="flex-shrink-0">
                                <div
                                    className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-emerald-600 to-emerald-500 rounded-xl flex items-center justify-center shadow-luxury">
                                    <CalendarIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white"/>
                                </div>
                            </div>
                            <div className="ml-3 sm:ml-4">
                                <p className="text-xs sm:text-sm font-body font-medium text-emerald-600/70">Booked Sessions</p>
                                <p className="text-xl sm:text-2xl font-display font-bold text-accent-platinum">{bookedSessions}</p>
                            </div>
                        </a>
                    </div>
                </div>

                <div className="card-luxury p-4 sm:p-6 hover-lift">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <div
                                className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-emerald-600 to-emerald-500 rounded-xl flex items-center justify-center shadow-luxury">
                                <UserPlusIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white"/>
                            </div>
                        </div>
                        <div className="ml-3 sm:ml-4">
                            <p className="text-xs sm:text-sm font-body font-medium text-emerald-600/70">My Tokens</p>
                            <p className="text-xl sm:text-2xl font-display font-bold text-accent-platinum">{usersVenueTokens}</p>
                        </div>
                    </div>
                </div>
                {isAdmin && (
                    <>
                        <div className="card-luxury p-4 sm:p-6 hover-lift">
                            <div className="flex items-center">
                                <div className="flex-shrink-0">
                                    <div
                                        className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-emerald-600 to-emerald-500 rounded-xl flex items-center justify-center shadow-luxury">
                                        <UsersIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white"/>
                                    </div>
                                </div>
                                <div className="ml-3 sm:ml-4">
                                    <p className="text-xs sm:text-sm font-body font-medium text-emerald-600/70">Total Members</p>
                                    <p className="text-xl sm:text-2xl font-display font-bold text-accent-platinum">{totalMembers}</p>
                                </div>
                            </div>
                        </div>

                        <div 
                            className={`card-luxury p-4 sm:p-6 hover-lift cursor-pointer transition-all duration-300 ${
                                atRiskUsersCount > 0 
                                    ? 'border-performance-pulse/50 hover:border-performance-pulse/80 hover:shadow-[0_0_30px_rgba(255,7,58,0.2)]' 
                                    : ''
                            }`}
                            onClick={() => atRiskUsersCount > 0 && navigate('/dashboard/reports/users-at-risk')}
                        >
                            <div className="flex items-center">
                                <div className="flex-shrink-0">
                                    <div
                                        className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center ${
                                            atRiskUsersCount > 0 
                                                ? 'bg-gradient-to-br from-performance-pulse to-performance-energy shadow-luxury animate-pulse' 
                                                : 'bg-gradient-to-br from-emerald-600 to-emerald-500 shadow-luxury'
                                        }`}>
                                        {atRiskUsersCount > 0 ? (
                                            <ExclamationTriangleIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white"/>
                                        ) : (
                                            <ChartBarIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white"/>
                                        )}
                                    </div>
                                </div>
                                <div className="ml-3 sm:ml-4">
                                    <p className={`text-xs sm:text-sm font-body font-medium ${
                                        atRiskUsersCount > 0 ? 'text-performance-pulse' : 'text-emerald-600/70'
                                    }`}>
                                        {atRiskUsersCount > 0 ? 'At-Risk Users' : 'All Engaged'}
                                    </p>
                                    <p className={`text-xl sm:text-2xl font-display font-bold ${
                                        atRiskUsersCount > 0 ? 'text-performance-pulse' : 'text-accent-platinum'
                                    }`}>
                                        {atRiskUsersCount > 0 ? atRiskUsersCount : '✓'}
                                    </p>
                                </div>
                            </div>
                            {atRiskUsersCount > 0 && (
                                <div className="mt-2 text-xs font-body text-performance-pulse/80">
                                    Click to view details →
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div> )}
            {/* Expected money on the next run day, for members who signed up through the site (admins only) */}
            {isAdmin && (
                <div className="mb-6 sm:mb-8">
                    <UpcomingPaymentsCard venueKey={currentVenueKey} />
                </div>
            )}

            {/* Training Plan Widget - Show for all users */}
            <div className="mb-6 sm:mb-8">
                <TrainingPlanWidget />
            </div>
            
            {isAdmin && hasFeature("sessions") && (
                <div className="mb-8">
                    <UserAdminTable/>
                </div>
            )}

            {/* Add Session Modal */}
            {showAddSessionModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-gray-900 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center p-4 border-b border-gray-700">
                            <h3 className="text-lg font-semibold text-white">Add Session</h3>
                            <button
                                onClick={() => setShowAddSessionModal(false)}
                                className="text-gray-400 hover:text-white transition-colors p-1"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                          d="M6 18L18 6M6 6l12 12"/>
                                </svg>
                            </button>
                        </div>
                        <AddSession
                            handleClose={() => setShowAddSessionModal(false)}
                            Values={{
                                venue: {
                                    key: currentVenueKey
                                }
                            }}
                        />
                    </div>
                </div>
            )}
        </LayoutWrapper>
    );
}
